import type { BeforeSendFn, CaptureResult } from "posthog-js";

/**
 * Email scanner / link-sandbox fingerprint tagging (PLA-796).
 *
 * Aligns with the HogQL bot CTE on PostHog dashboard
 * "Site performance (screen/UA bot filter)" (id 2040795):
 *   utm_medium=email + 1920×1080 + Windows NT + Chrome + America/Los_Angeles
 *   + session has no $autocapture (client: no $autocapture seen yet).
 *
 * Tags matching events with `suspected_email_scanner: true`. Never drops events.
 * After the first `$autocapture`, later events are not tagged (already-sent events
 * keep whatever property they were sent with).
 *
 * Kill switch: `NEXT_PUBLIC_POSTHOG_EMAIL_SCANNER_TAG`. Unset defaults to enabled.
 * Set to `false` / `0` / `no` (or empty) to disable tagging.
 */

export const SUSPECTED_EMAIL_SCANNER_PROPERTY = "suspected_email_scanner";

export const EMAIL_SCANNER_SCREEN_WIDTH = 1920;
export const EMAIL_SCANNER_SCREEN_HEIGHT = 1080;
export const EMAIL_SCANNER_TIMEZONE = "America/Los_Angeles";

const AUTOCAPTURE_SEEN_KEY = "playmath.emailScannerAutocaptureSeen";
const UTM_MEDIUM_KEY = "playmath.utmMedium";

export type EmailScannerFingerprintInput = {
  utmMedium: string | null | undefined;
  screenWidth: number | null | undefined;
  screenHeight: number | null | undefined;
  userAgent: string | null | undefined;
  timezone: string | null | undefined;
  hasSeenAutocapture: boolean;
};

/** Kill switch for email-scanner tagging. Defaults to enabled when unset. */
export function parsePosthogEmailScannerTagEnabled(value: string | undefined): boolean {
  if (value === undefined) return true;
  const v = value.toLowerCase().trim();
  if (v === "" || v === "false" || v === "0" || v === "no") return false;
  return v === "true" || v === "1" || v === "yes";
}

export function isEmailUtmMedium(utmMedium: string | null | undefined): boolean {
  return utmMedium?.toLowerCase() === "email";
}

export function isScannerScreenResolution(
  width: number | null | undefined,
  height: number | null | undefined
): boolean {
  return width === EMAIL_SCANNER_SCREEN_WIDTH && height === EMAIL_SCANNER_SCREEN_HEIGHT;
}

/** Matches dashboard: UA ILIKE '%Windows NT%' AND ILIKE '%Chrome%'. */
export function isWindowsChromeUserAgent(userAgent: string | null | undefined): boolean {
  if (!userAgent) return false;
  return /Windows NT/i.test(userAgent) && /Chrome/i.test(userAgent);
}

export function isLosAngelesTimezone(timezone: string | null | undefined): boolean {
  return timezone === EMAIL_SCANNER_TIMEZONE;
}

/**
 * Dashboard-aligned rule: email UTM + 1920×1080 + Windows NT/Chrome UA +
 * America/Los_Angeles + no $autocapture seen yet in this tab.
 */
export function matchesEmailScannerFingerprint(input: EmailScannerFingerprintInput): boolean {
  if (input.hasSeenAutocapture) return false;
  if (!isEmailUtmMedium(input.utmMedium)) return false;
  if (!isScannerScreenResolution(input.screenWidth, input.screenHeight)) return false;
  if (!isWindowsChromeUserAgent(input.userAgent)) return false;
  if (!isLosAngelesTimezone(input.timezone)) return false;
  return true;
}

function readStringProperty(properties: Record<string, unknown>, key: string): string | null {
  const value = properties[key];
  return typeof value === "string" ? value : null;
}

function readNumberProperty(properties: Record<string, unknown>, key: string): number | null {
  const value = properties[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  // PostHog sometimes stores screen dims as strings; HogQL uses toInt().
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function utmMediumFromEventProperties(properties: Record<string, unknown>): string | null {
  return (
    readStringProperty(properties, "utm_medium") ??
    readStringProperty(properties, "$initial_utm_medium") ??
    getPersistedUtmMedium()
  );
}

export function screenDimensionsFromEventProperties(properties: Record<string, unknown>): {
  width: number | null;
  height: number | null;
} {
  return {
    width: readNumberProperty(properties, "$screen_width"),
    height: readNumberProperty(properties, "$screen_height"),
  };
}

export function userAgentFromEventProperties(properties: Record<string, unknown>): string | null {
  return (
    readStringProperty(properties, "$raw_user_agent") ??
    readStringProperty(properties, "$useragent") ??
    readStringProperty(properties, "$user_agent")
  );
}

export function timezoneFromEventProperties(properties: Record<string, unknown>): string | null {
  return readStringProperty(properties, "$timezone");
}

export function fingerprintInputFromEvent(
  properties: Record<string, unknown>,
  options: {
    hasSeenAutocapture: boolean;
    userAgent?: string | null;
    timezone?: string | null;
    screenWidth?: number | null;
    screenHeight?: number | null;
  }
): EmailScannerFingerprintInput {
  const fromEvent = screenDimensionsFromEventProperties(properties);
  return {
    utmMedium: utmMediumFromEventProperties(properties),
    screenWidth: fromEvent.width ?? options.screenWidth ?? null,
    screenHeight: fromEvent.height ?? options.screenHeight ?? null,
    userAgent: userAgentFromEventProperties(properties) ?? options.userAgent ?? null,
    timezone: timezoneFromEventProperties(properties) ?? options.timezone ?? null,
    hasSeenAutocapture: options.hasSeenAutocapture,
  };
}

export function persistUtmMediumFromLocation(location: Pick<Location, "search">): void {
  if (typeof window === "undefined") return;
  try {
    const medium = new URLSearchParams(location.search).get("utm_medium");
    if (medium) window.sessionStorage.setItem(UTM_MEDIUM_KEY, medium);
  } catch {
    // Non-critical: sessionStorage may be unavailable
  }
}

export function getPersistedUtmMedium(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(UTM_MEDIUM_KEY);
  } catch {
    return null;
  }
}

function hasPersistedAutocaptureSeen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(AUTOCAPTURE_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function setPersistedAutocaptureSeen(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(AUTOCAPTURE_SEEN_KEY, "1");
  } catch {
    // Non-critical
  }
}

function tagEvent(event: CaptureResult): CaptureResult {
  return {
    ...event,
    properties: {
      ...event.properties,
      [SUSPECTED_EMAIL_SCANNER_PROPERTY]: true,
    },
  };
}

function browserUserAgent(): string | null {
  if (typeof navigator === "undefined") return null;
  return navigator.userAgent || null;
}

function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function browserScreenDimensions(): { width: number | null; height: number | null } {
  if (typeof window === "undefined") {
    return { width: null, height: null };
  }
  return {
    width: window.screen?.width ?? null,
    height: window.screen?.height ?? null,
  };
}

export class EmailScannerTagger {
  private hasSeenAutocapture = false;
  private readonly enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
    if (typeof window !== "undefined" && hasPersistedAutocaptureSeen()) {
      this.hasSeenAutocapture = true;
    }
  }

  installSessionHooks(): void {
    if (!this.enabled || typeof window === "undefined") return;
    persistUtmMediumFromLocation(window.location);
  }

  readonly beforeSend: BeforeSendFn = (event) => {
    if (!event) return null;
    if (!this.enabled) return event;

    if (event.event === "$autocapture") {
      this.hasSeenAutocapture = true;
      setPersistedAutocaptureSeen();
      return event;
    }

    if (this.hasSeenAutocapture || hasPersistedAutocaptureSeen()) {
      this.hasSeenAutocapture = true;
      return event;
    }

    const screen = browserScreenDimensions();
    const input = fingerprintInputFromEvent(event.properties ?? {}, {
      hasSeenAutocapture: this.hasSeenAutocapture,
      userAgent: browserUserAgent(),
      timezone: browserTimezone(),
      screenWidth: screen.width,
      screenHeight: screen.height,
    });

    if (matchesEmailScannerFingerprint(input)) {
      return tagEvent(event);
    }

    return event;
  };
}
